// Environment Configuration for Super Wear Frontend
// Defaults live in `.env` (see `.env.example`). CRA only exposes `REACT_APP_*` to the bundle.

// API base URL should be origin + `/api` (e.g. http://localhost:5000/api).
// If REACT_APP_API_URL is only the host (no path), append `/api` so requests hit `/api/admin/...`.
function normalizeApiBaseUrl(raw) {
  if (!raw || typeof raw !== 'string') return raw;
  const u = raw.trim().replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(u)) return raw.trim();
  try {
    const parsed = new URL(u);
    const path = (parsed.pathname || '/').replace(/\/+$/, '') || '/';
    // CRA dev server is usually :3000; API must not point there or every fetch hits the React app (404).
    if (parsed.port === '3000') {
      const apiPath = /^\/api(\/|$)/.test(path) ? path : path === '/' ? '/api' : `/api${path}`;
      return `${parsed.protocol}//${parsed.hostname}:5000${apiPath}`;
    }
    if (/^\/api(\/|$)/.test(path)) return u;
    if (path === '/') return `${u}/api`;
  } catch {
    /* ignore */
  }
  return u;
}

/** CRA dev server (localhost or LAN IP on :3000). API uses same-origin `/api` + package.json proxy → backend. */
function isBrowserOnCraDevServer() {
  if (typeof window === 'undefined') return false;
  return String(window.location.port) === '3000';
}

/**
 * Storefront on superwear.pk / superwear.net — API stays on api.superwear.net (not same-origin /api).
 * Used when REACT_APP_API_URL was missing at build time (Hostinger deploy without env).
 */
const STOREFRONT_HOSTS_DEDICATED_API = [
  'superwear.pk',
  'www.superwear.pk',
  'superwear.net',
  'www.superwear.net',
];

const DEDICATED_PRODUCTION_API_BASE =
  (process.env.REACT_APP_DEFAULT_API_URL &&
    normalizeApiBaseUrl(process.env.REACT_APP_DEFAULT_API_URL.trim())) ||
  'https://api.superwear.net/api';

function usesDedicatedProductionApi(hostname) {
  if (!hostname || typeof hostname !== 'string') return false;
  return STOREFRONT_HOSTS_DEDICATED_API.includes(hostname.toLowerCase());
}

function dedicatedProductionApiOrigin() {
  try {
    return new URL(DEDICATED_PRODUCTION_API_BASE).origin;
  } catch {
    return 'https://api.superwear.net';
  }
}

/** When REACT_APP_API_URL is unset: local CRA (:3000) → same-origin /api; deployed HTTPS → same origin /api. */
function isLocalDevHostname() {
  if (typeof window === 'undefined') return false;
  const { hostname, port } = window.location;
  return (
    port === '3000' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  );
}

/**
 * If the app is opened via LAN IP (e.g. http://192.168.1.5:3000) but .env still has
 * REACT_APP_API_URL=http://localhost:5000/api, replace loopback with the page hostname
 * so the phone/other device talks to the dev machine, not itself.
 */
function rewriteLoopbackApiHostForCurrentPage(baseUrl) {
  if (typeof window === 'undefined' || !baseUrl || typeof baseUrl !== 'string') return baseUrl;
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return baseUrl;
  try {
    const u = new URL(baseUrl);
    if (u.hostname !== 'localhost' && u.hostname !== '127.0.0.1') return baseUrl;
    u.hostname = hostname;
    return u.toString().replace(/\/+$/, '');
  } catch {
    return baseUrl;
  }
}

const getApiBaseUrlValue = () => {
  if (typeof window !== 'undefined' && isBrowserOnCraDevServer()) {
    return `${window.location.origin}/api`;
  }
  if (process.env.REACT_APP_API_URL) {
    const normalized = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);
    return rewriteLoopbackApiHostForCurrentPage(normalized);
  }
  if (typeof window !== 'undefined') {
    if (usesDedicatedProductionApi(window.location.hostname)) {
      return DEDICATED_PRODUCTION_API_BASE;
    }
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:5000/api';
};

function getEmailServiceUrlValue() {
  if (typeof window !== 'undefined' && isBrowserOnCraDevServer()) {
    return `${window.location.origin}/api/email`;
  }
  if (process.env.REACT_APP_EMAIL_SERVICE_URL) {
    return rewriteLoopbackApiHostForCurrentPage(process.env.REACT_APP_EMAIL_SERVICE_URL);
  }
  if (typeof window !== 'undefined' && usesDedicatedProductionApi(window.location.hostname)) {
    return `${dedicatedProductionApiOrigin()}/email`;
  }
  return rewriteLoopbackApiHostForCurrentPage('http://localhost:5000/api/email');
}

const environment = {
  // Backend API Configuration - use getter for runtime hostname
  get API_URL() { return getApiBaseUrlValue(); },
  FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL || (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname}:3000` : 'http://localhost:3000'),
  
  // Payment Gateway Configuration
  STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  PAYPAL_CLIENT_ID: process.env.REACT_APP_PAYPAL_CLIENT_ID || '',
  
  // Cloud Storage Configuration
  CLOUDINARY_CLOUD_NAME: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || '',
  CLOUDINARY_UPLOAD_PRESET: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || '',
  
  // Email Configuration
  get EMAIL_SERVICE_URL() {
    return getEmailServiceUrlValue();
  },
  
  // Analytics Configuration
  GOOGLE_ANALYTICS_ID: process.env.REACT_APP_GOOGLE_ANALYTICS_ID || '',
  FACEBOOK_PIXEL_ID: process.env.REACT_APP_FACEBOOK_PIXEL_ID || '',
  
  // Social Media Configuration
  FACEBOOK_APP_ID: process.env.REACT_APP_FACEBOOK_APP_ID || '',
  GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
  
  // WhatsApp Business Configuration
  WHATSAPP_BUSINESS_NUMBER: process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER || '923218897842',
  WHATSAPP_API_URL: process.env.REACT_APP_WHATSAPP_API_URL || 'https://api.whatsapp.com',
  
  // Development Configuration
  ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'development',
  DEBUG_MODE: process.env.REACT_APP_DEBUG_MODE === 'true',
  ENABLE_LOGGING: process.env.REACT_APP_ENABLE_LOGGING === 'true',
  
  // Feature Flags
  ENABLE_PAYMENT: process.env.REACT_APP_ENABLE_PAYMENT !== 'false',
  ENABLE_WISHLIST: process.env.REACT_APP_ENABLE_WISHLIST !== 'false',
  ENABLE_REVIEWS: process.env.REACT_APP_ENABLE_REVIEWS !== 'false',
  ENABLE_CHAT: process.env.REACT_APP_ENABLE_CHAT !== 'false',
  ENABLE_NOTIFICATIONS: process.env.REACT_APP_ENABLE_NOTIFICATIONS !== 'false',
};

// Helper functions
export const isDevelopment = () => environment.ENVIRONMENT === 'development';
export const isProduction = () => environment.ENVIRONMENT === 'production';
export const isStaging = () => environment.ENVIRONMENT === 'staging';

export const log = (message, data = null) => {
  if (environment.ENABLE_LOGGING) {
    if (data) {
      console.log(`[Super Wear] ${message}`, data);
    } else {
      console.log(`[Super Wear] ${message}`);
    }
  }
};

export const logError = (message, error = null) => {
  if (environment.ENABLE_LOGGING) {
    if (error) {
      console.error(`[Super Wear Error] ${message}`, error);
    } else {
      console.error(`[Super Wear Error] ${message}`);
    }
  }
};

export const logWarning = (message, data = null) => {
  if (environment.ENABLE_LOGGING) {
    if (data) {
      console.warn(`[Super Wear Warning] ${message}`, data);
    } else {
      console.warn(`[Super Wear Warning] ${message}`);
    }
  }
};

// API URL helpers
export const getApiUrl = (endpoint = '') => {
  return `${environment.API_URL}${endpoint}`;
};

export const getFullUrl = (path = '') => {
  return `${environment.FRONTEND_URL}${path}`;
};

// Direct API URL export - use getter for runtime hostname (works with network IP)
export const getApiBaseUrl = () => getApiBaseUrlValue();

// Origin only (no /api) — use with `${origin}/api/...` in admin hooks. Uses same normalization as API_URL.
/**
 * Max `limit` per request for admin order list aggregations (legacy single-page callers).
 * Many APIs return 400 above 100 ("Limit must be between 1 and 100").
 * Paginated loaders use `ADMIN_LIST_PAGE_LIMIT` in `admin/utils/adminOrdersBulkFetch.js`.
 * `REACT_APP_ADMIN_ORDERS_AGG_LIMIT` is clamped to 1–100 when set.
 */
export const getAdminOrdersAggLimit = () => {
  const raw = process.env.REACT_APP_ADMIN_ORDERS_AGG_LIMIT;
  const cap = 100;
  if (raw == null || String(raw).trim() === '') return cap;
  const n = parseInt(String(raw), 10);
  if (!Number.isFinite(n) || n < 1) return cap;
  return Math.min(Math.max(n, 1), cap);
};

export const getApiBaseUrlWithoutPath = () => {
  if (typeof window !== 'undefined' && isBrowserOnCraDevServer()) {
    return window.location.origin;
  }
  if (process.env.REACT_APP_API_URL) {
    let normalized = normalizeApiBaseUrl(process.env.REACT_APP_API_URL.trim());
    normalized = rewriteLoopbackApiHostForCurrentPage(normalized);
    if (normalized && typeof normalized === 'string') {
      try {
        return new URL(normalized).origin;
      } catch {
        /* fall through */
      }
    }
    const raw = process.env.REACT_APP_API_URL.trim();
    return raw.replace(/\/api\/?$/, '') || raw;
  }
  if (typeof window !== 'undefined') {
    if (usesDedicatedProductionApi(window.location.hostname)) {
      return dedicatedProductionApiOrigin();
    }
    return window.location.origin;
  }
  return 'http://localhost:5000';
};

// Feature check helpers
export const isFeatureEnabled = (feature) => {
  return environment[`ENABLE_${feature.toUpperCase()}`] !== false;
};

// Payment configuration helpers
export const getPaymentConfig = () => ({
  stripe: {
    publishableKey: environment.STRIPE_PUBLISHABLE_KEY,
  },
  paypal: {
    clientId: environment.PAYPAL_CLIENT_ID,
  },
});

// WhatsApp configuration helpers
export const getWhatsAppConfig = () => ({
  businessNumber: environment.WHATSAPP_BUSINESS_NUMBER,
  apiUrl: environment.WHATSAPP_API_URL,
});

export default environment; 